import { Device } from "@twilio/voice-sdk";
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { gatewayPhone } from "src/gateways/gatewayPhone";
import { useGlobalSnackbar } from "src/context/SnackbarContext";
import { useRequireMicrophoneForCall } from "@components/internal-contacts/_hooks/useRequireMicrophoneForCall";
import { getCallStartMediaErrorMessage } from "@components/internal-contacts/_utils/requestMicrophoneAccess.utils";

/**
 * Thin Twilio Client leg used ONLY for internal calls that cross the web<->mobile boundary
 * (mobile has no LiveKit engine anymore after the Twilio consolidation). web<->web internal
 * calls keep using InternalWebRTCContext/LiveKit untouched — this context is deliberately
 * separate so the two call state machines never have to share a reducer. See
 * VirtualAssistantContext.tsx for the reference Device lifecycle this mirrors (register,
 * tokenWillExpire, incoming), simplified to a single bare-identity Device instead of one
 * per company phone number.
 */

type InternalTwilioCallStatus = "idle" | "ringing-outgoing" | "ringing-incoming" | "in-call";

/** Same shape as InternalWebRTC `internalCallView` so CallWidget can treat both modes alike. */
export type InternalTwilioCallView = "incoming" | "outgoing-ringing" | "in-call";

type InternalTwilioCallState = {
  status: InternalTwilioCallStatus;
  remoteIdentity: string | null;
  remoteName: string | null;
  remoteCompanyName: string | null;
  isMuted: boolean;
  callDuration: number;
};

const looksLikeTechnicalIdentity = (value?: string | null): boolean => {
  if (!value) return true;
  const trimmed = value.trim();
  if (trimmed.startsWith("client:")) return true;
  const parts = trimmed.split(":");
  return parts.length >= 2 && /^[0-9a-f-]{16,}$/i.test(parts[0] ?? "");
};

const formatPeerDisplayName = (
  name?: string | null,
  companyName?: string | null,
  identityFallback?: string | null,
): string => {
  const person = name?.trim();
  const company = companyName?.trim();
  if (person && company) return `${company} - ${person}`;
  if (person) return person;
  if (company) return company;
  if (identityFallback && !looksLikeTechnicalIdentity(identityFallback)) return identityFallback;
  return "Internal contact";
};

const readCallDisplayParam = (call: any, ...keys: string[]): string | null => {
  const custom = call?.customParameters;
  for (const key of keys) {
    const fromMap = typeof custom?.get === "function" ? custom.get(key) : undefined;
    const value = (fromMap ?? custom?.[key] ?? call?.parameters?.[key] ?? "").toString().trim();
    if (value) return value;
  }
  return null;
};

const statusToCallView = (status: InternalTwilioCallStatus): InternalTwilioCallView | null => {
  if (status === "ringing-incoming") return "incoming";
  if (status === "ringing-outgoing") return "outgoing-ringing";
  if (status === "in-call") return "in-call";
  return null;
};

const INITIAL_STATE: InternalTwilioCallState = {
  status: "idle",
  remoteIdentity: null,
  remoteName: null,
  remoteCompanyName: null,
  isMuted: false,
  callDuration: 0,
};

interface InternalTwilioCallContextValue extends InternalTwilioCallState {
  internalCallView: InternalTwilioCallView | null;
  peerDisplayName: string;
  makeCall: (targetUid: string, targetCompanyId: string | number, targetName?: string) => Promise<void>;
  acceptCall: () => void;
  rejectCall: () => void;
  hangupCall: () => void;
  toggleMute: () => void;
}

const InternalTwilioCallContext = createContext<InternalTwilioCallContextValue | undefined>(undefined);

export const useInternalTwilioCall = (): InternalTwilioCallContextValue => {
  const ctx = useContext(InternalTwilioCallContext);
  if (!ctx) throw new Error("useInternalTwilioCall must be used within InternalTwilioCallProvider");
  return ctx;
};

export const useOptionalInternalTwilioCall = (): InternalTwilioCallContextValue | null => {
  return useContext(InternalTwilioCallContext) ?? null;
};

const HEARTBEAT_INTERVAL_MS = 15000;

type Props = { children: ReactNode };

export const InternalTwilioCallProvider = ({ children }: Props) => {
  const { uid, company, firstName, lastName } = useSelector((state: any) => state.userState);
  const companyId = company?.id ?? null;
  const requireMicrophone = useRequireMicrophoneForCall();
  const { showSnackbar } = useGlobalSnackbar();

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<any>(null);
  const registeringRef = useRef(false);
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callStartRef = useRef<number | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [state, setState] = useState<InternalTwilioCallState>(INITIAL_STATE);

  const updateState = useCallback((patch: Partial<InternalTwilioCallState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const fetchFromBackend = useCallback(async (endpoint: string, method: "GET" | "POST" = "GET", body?: any, cId?: string | number) => {
    const response = await gatewayPhone.request({
      url: endpoint,
      method,
      data: body,
      headers: { ...(cId ? { "x-company-id": String(cId) } : {}) },
    });
    return response.data;
  }, []);

  const stopCallTimer = useCallback(() => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    callStartRef.current = null;
  }, []);

  const startCallTimer = useCallback(() => {
    stopCallTimer();
    callStartRef.current = Date.now();
    callTimerRef.current = setInterval(() => {
      const start = callStartRef.current;
      if (!start) return;
      updateState({ callDuration: Math.floor((Date.now() - start) / 1000) });
    }, 1000);
  }, [stopCallTimer, updateState]);

  const cleanupCallState = useCallback(() => {
    callRef.current = null;
    stopCallTimer();
    setState(INITIAL_STATE);
  }, [stopCallTimer]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(
    (identity: string, resolvedCompanyId: string | number) => {
      stopHeartbeat();
      const send = () => {
        fetchFromBackend("/twilio/heartbeat", "POST", { identity }, resolvedCompanyId).catch(() => { });
      };
      send();
      heartbeatRef.current = setInterval(send, HEARTBEAT_INTERVAL_MS);
    },
    [fetchFromBackend, stopHeartbeat],
  );

  const bindCallEvents = useCallback(
    (call: any) => {
      let ended = false;
      const handleEnd = () => {
        if (ended) return;
        ended = true;
        cleanupCallState();
      };

      call.on("accept", () => {
        updateState({ status: "in-call" });
        startCallTimer();
      });
      call.on("disconnect", handleEnd);
      call.on("cancel", handleEnd);
      call.on("reject", handleEnd);
      call.on("error", handleEnd);
    },
    [cleanupCallState, startCallTimer, updateState],
  );

  const initializeDevice = useCallback(async () => {
    if (!uid || !companyId) return;
    if (deviceRef.current || registeringRef.current) return;

    registeringRef.current = true;
    const identity = `${uid}:${companyId}`;

    try {
      const { token } = await fetchFromBackend(`/twilio/token?companyId=${encodeURIComponent(companyId)}`, "GET", undefined, companyId);

      const device = new Device(token, {
        logLevel: "error",
        allowIncomingWhileBusy: true,
      });

      device.on("registered", () => {
        console.log("[internal-twilio-call] registered", identity);
        startHeartbeat(identity, companyId);
      });

      device.on("unregistered", () => {
        console.log("[internal-twilio-call] unregistered", identity);
        stopHeartbeat();
      });

      device.on("tokenWillExpire", async () => {
        try {
          const { token: newToken } = await fetchFromBackend(`/twilio/token?companyId=${encodeURIComponent(companyId)}`, "GET", undefined, companyId);
          device.updateToken(newToken);
        } catch (err) {
          console.warn("[internal-twilio-call] token refresh failed", err);
        }
      });

      device.on("error", (err: any) => {
        console.error("[internal-twilio-call-error]", identity, err);
      });

      device.on("incoming", (call: any) => {
        const remoteName = readCallDisplayParam(call, "callerName", "CallerName");
        const remoteCompanyName = readCallDisplayParam(
          call,
          "callerCompanyName",
          "CallerCompanyName",
          "companyName",
        );
        const callerDisplayName = readCallDisplayParam(
          call,
          "callerDisplayName",
          "CallerDisplayName",
        );
        console.log("[internal-twilio-call] incoming", {
          identity,
          from: call.parameters?.From,
          remoteName,
          remoteCompanyName,
          callerDisplayName,
        });
        callRef.current = call;
        bindCallEvents(call);
        updateState({
          status: "ringing-incoming",
          remoteIdentity: call.parameters?.From ?? null,
          remoteName: callerDisplayName || remoteName,
          remoteCompanyName,
        });
      });

      await device.register();
      deviceRef.current = device;
    } catch (err) {
      console.error("[initialize-internal-twilio-device-error]", err);
    } finally {
      registeringRef.current = false;
    }
  }, [uid, companyId, fetchFromBackend, startHeartbeat, stopHeartbeat, bindCallEvents, updateState]);

  useEffect(() => {
    void initializeDevice();

    return () => {
      stopHeartbeat();
      stopCallTimer();
      deviceRef.current?.destroy();
      deviceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, companyId]);

  const makeCall = useCallback(
    async (targetUid: string, targetCompanyId: string | number, targetName?: string) => {
      const canUseMicrophone = await requireMicrophone();
      if (!canUseMicrophone) return;

      if (!deviceRef.current) {
        await initializeDevice();
      }

      const device = deviceRef.current;
      if (!device) {
        console.warn("[internal-twilio-call] cannot place call — device not initialized");
        return;
      }

      try {
        const call = await device.connect({
          params: {
            internalTargetUid: targetUid,
            companyId: String(targetCompanyId),
            callerName: [firstName, lastName].filter(Boolean).join(" ") || undefined,
            callerCompanyName: company?.businessName ?? undefined,
          },
        });

        updateState({ status: "ringing-outgoing", remoteIdentity: targetUid, remoteName: targetName ?? null });
        callRef.current = call;
        bindCallEvents(call);
      } catch (err) {
        console.error("[internal-twilio-call] makeCall failed", err);
        showSnackbar(getCallStartMediaErrorMessage(err), "error", 6000, "bottom-center");
        cleanupCallState();
      }
    },
    [initializeDevice, bindCallEvents, cleanupCallState, company?.businessName, firstName, lastName, requireMicrophone, showSnackbar, updateState],
  );

  const acceptCall = useCallback(() => {
    const call = callRef.current;
    if (!call) return;

    void (async () => {
      const canUseMicrophone = await requireMicrophone();
      if (!canUseMicrophone) return;

      try {
        call.accept();
        updateState({ status: "in-call" });
        startCallTimer();
      } catch (err) {
        console.error("[internal-twilio-call] accept failed", err);
        showSnackbar(getCallStartMediaErrorMessage(err), "error", 6000, "bottom-center");
      }
    })();
  }, [requireMicrophone, showSnackbar, startCallTimer, updateState]);

  const rejectCall = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    try {
      call.reject();
    } catch (err) {
      console.error("[internal-twilio-call] reject failed", err);
    } finally {
      cleanupCallState();
    }
  }, [cleanupCallState]);

  const hangupCall = useCallback(() => {
    const call = callRef.current;
    try {
      call?.disconnect();
    } catch (err) {
      console.error("[internal-twilio-call] hangup failed", err);
    } finally {
      cleanupCallState();
    }
  }, [cleanupCallState]);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !state.isMuted;
    try {
      call.mute(next);
      updateState({ isMuted: next });
    } catch (err) {
      console.error("[internal-twilio-call] toggle mute failed", err);
    }
  }, [state.isMuted, updateState]);

  const value = useMemo<InternalTwilioCallContextValue>(
    () => ({
      ...state,
      internalCallView: statusToCallView(state.status),
      peerDisplayName:
        state.remoteName && !looksLikeTechnicalIdentity(state.remoteName)
          ? state.remoteName
          : formatPeerDisplayName(null, state.remoteCompanyName, state.remoteIdentity),
      makeCall,
      acceptCall,
      rejectCall,
      hangupCall,
      toggleMute,
    }),
    [state, makeCall, acceptCall, rejectCall, hangupCall, toggleMute],
  );

  return <InternalTwilioCallContext.Provider value={value}>{children}</InternalTwilioCallContext.Provider>;
};
